/* globals process */

import store from '@/store';

export default (to, from, next) => {
  // Google Analytics Call
  window.dataLayer.push({
    event: 'VirtualPageview',
    virtualPageURL: to.path,
    virtualPageTitle: to.name,
  });

  if (to.name === 'loading') {
    next();
    return;
  }

  // If the user has a JWT, they are authenticated and can proceed to whatever they want to view.
  if (store.getters.userJWT) {
    // User is logged in, so we can continue to any route
    proceedToApplication();
  } else {
    // They don't have one, let's try and restore an existing session from a cookie
    restoreSession();
  }

  function redirectToAuthPage() {
    // If we have noticed the user has a choose group flag set, go to the select group
    if (store.getters.getChooseGroup) {
      if (to.name !== 'selectGroup') {
        next({
          name: 'selectGroup',
        });
      } else {
        next();
      }
      return;
    }

    if (store.getters.getMemberMigrationComplete) {
      next({
        name: 'federateHQY',
      });
      return;
    }

    const REDIRECT_URL_IS_NONE =
      process.env.VUE_APP_LOGIN_REDIRECT_URL === 'none';

    // User not authenticated, so redirect to login page
    // The following pages can be viewed if they are unauthenticated
    if (isUnprotectedPage()) {
      // If the user logs out and the cobranded partner SSOs in, this redirects to their login page
      if (!isLoggedOut() && !REDIRECT_URL_IS_NONE && !isSSOError() && !isSA()) {
        window.location.href = process.env.VUE_APP_LOGIN_REDIRECT_URL;
      } else {
        // If not, just go to one of the unauthenticated pages
        next();
      }
    } else {
      // The user has tried to reach a page that requires authentication, deny this and go to login
      if (!isLoggedOut() && !REDIRECT_URL_IS_NONE) {
        window.location.href = process.env.VUE_APP_LOGIN_REDIRECT_URL;
      } else {
        next({
          name: 'login',
          params: {
            routeAfterLogin: to.name ? to.name : 'accounts',
          },
        });
      }
    }
  }

  function restoreSession() {
    // Restore session action calls out to the auth service method getSession() and returns the promise
    store
      .dispatch('restoreSession')
      .then(() => {
        // If this resolves, then the user had a cookie and can continue to the application
        // If it is rejected, we redirect to the Auth page
        proceedToApplication();
      })
      .catch(redirectToAuthPage);
  }

  function proceedToApplication() {
    if (needsActivation()) {
      // If user needs to activate their HSA, redirect to the activateHSA page
      if (isActivateHSA()) {
        next();
      } else {
        next({ name: 'activateHSA', params: { step: 1 } });
      }
    } else if (isActivateHSA()) {
      // If the user does not need to activate their HSA but they're on that screen,
      // redirect them to the accounts page
      next({
        name: 'accounts',
      });
    } else if (isUnprotectedPage()) {
      // If the user is on an unprotected page but they are logged in,
      // redirect them to the accounts page
      if (isOnlineApp() || isInterestRate()) {
        // But if they're on the online app page, let them stay there
        next();
      } else {
        next({
          name: 'accounts',
        });
      }
    } else {
      // Catch-all: proceed with routing as usual
      next();
    }
  }

  function isUnprotectedPage() {
    return (
      isLoginOrRegister() || isLoggedOut() || isSSOError() || isOnlineApp() || isLoading() || isInterestRate() || isSA()
    );
  }

  function isLoginOrRegister() {
    // The "to" object is passed in from the route guard
    return to.name === 'login' || to.name === 'register';
  }

  function isActivateHSA() {
    return to.name === 'activateHSA';
  }

  function isLoggedOut() {
    return to.name === 'loggedOut';
  }

  function isSSOError() {
    return to.name === 'ssoError';
  }

  function isOnlineApp() {
    return (
      to.name === 'submitOnlineApp' || to.name === 'submitOnlineAppStepper'
    );
  }

  function isLoading() {
    return to.name === 'loading';
  }

  function needsActivation() {
    return store.getters.user.isNeedToActivate;
  }

  function isInterestRate() {
    return to.name === 'interestRate';
  }

  function isSA() {
    return to.name === 'getSaId';
  }
};
